/**
 * Team Store for GhostFlow RBAC system.
 * Manages current team context, permissions, and per-section access using Zustand.
 */

import type { AccessLevel, Section, UserAccessResponse } from '@/api/access'
import type { Team, TeamRole, TeamWithRole } from '@/api/teams'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// =============================================================================
// Types
// =============================================================================

interface TeamState {
  // Current team context
  currentTeamId: string | null
  currentTeam: Team | null
  currentRole: TeamRole | null
  teams: TeamWithRole[]

  // Per-section access (Phase 2.6)
  access: UserAccessResponse | null

  // Actions
  setCurrentTeam: (team: Team, role: TeamRole) => void
  setTeams: (teams: TeamWithRole[]) => void
  switchTeam: (teamId: string) => void
  clearTeamContext: () => void
  setAccess: (access: UserAccessResponse) => void
  clearAccess: () => void

  // Permission helpers (legacy role-based)
  hasPermission: (requiredRole: TeamRole) => boolean
  canManageTeam: () => boolean
  canInviteMembers: () => boolean
  canManageMembers: () => boolean
  canEditResources: () => boolean
  isOwner: () => boolean

  // Per-section access helpers (Phase 2.6)
  canAccessSection: (section: Section) => boolean
  canCreateIn: (section: Section) => boolean
  getAccessLevel: (section: Section) => AccessLevel
  isColumnHidden: (column: string) => boolean
  getHiddenColumns: () => string[]
}

// =============================================================================
// Role Hierarchy
// =============================================================================

const roleHierarchy: Record<TeamRole, number> = {
  viewer: 0,
  member: 1,
  manager: 2,
  admin: 3,
  owner: 4,
}

// =============================================================================
// Store
// =============================================================================

export const useTeamStore = create<TeamState>()(
  persist(
    (set, get) => ({
      // State
      currentTeamId: null,
      currentTeam: null,
      currentRole: null,
      teams: [],
      access: null,

      // Actions
      setCurrentTeam: (team, role) =>
        set({
          currentTeamId: team.id,
          currentTeam: team,
          currentRole: role,
        }),

      setTeams: (teams) => set({ teams }),

      switchTeam: (teamId) => {
        const { teams } = get()
        const team = teams.find((t) => t.id === teamId)
        if (team) {
          set({
            currentTeamId: team.id,
            currentTeam: team,
            currentRole: team.myRole,
            access: null, // Clear access on team switch — will be reloaded
          })
        }
      },

      clearTeamContext: () =>
        set({
          currentTeamId: null,
          currentTeam: null,
          currentRole: null,
          teams: [],
          access: null,
        }),

      setAccess: (access) => set({ access }),

      clearAccess: () => set({ access: null }),

      // Legacy role-based permission helpers
      hasPermission: (requiredRole) => {
        const { currentRole } = get()
        if (!currentRole) return false
        return roleHierarchy[currentRole] >= roleHierarchy[requiredRole]
      },

      canManageTeam: () => get().hasPermission('admin'),
      canInviteMembers: () => get().hasPermission('admin'),
      canManageMembers: () => get().hasPermission('admin'),
      canEditResources: () => get().hasPermission('member'),
      isOwner: () => get().currentRole === 'owner',

      // Per-section access helpers (Phase 2.6)
      canAccessSection: (section: Section) => {
        const { access, currentRole } = get()
        // Owner/Admin always have full access
        if (currentRole === 'owner' || currentRole === 'admin') return true
        if (!access) return true // Not loaded yet — assume access to avoid flash
        const sectionConfig = access.sections.find((s) => s.section === section)
        return sectionConfig ? sectionConfig.accessLevel !== 'none' : false
      },

      canCreateIn: (section: Section) => {
        const { access, currentRole } = get()
        if (currentRole === 'owner' || currentRole === 'admin') return true
        if (!access) return true
        const sectionConfig = access.sections.find((s) => s.section === section)
        return sectionConfig ? sectionConfig.canCreate : false
      },

      getAccessLevel: (section: Section) => {
        const { access, currentRole } = get()
        if (currentRole === 'owner' || currentRole === 'admin') return 'full'
        if (!access) return 'full' // Default until loaded
        const sectionConfig = access.sections.find((s) => s.section === section)
        return sectionConfig ? sectionConfig.accessLevel : 'none'
      },

      isColumnHidden: (column: string) => {
        const { access } = get()
        if (!access) return false
        const reports = access.sections.find((s) => s.section === 'reports')
        return reports ? reports.hiddenColumns.includes(column) : false
      },

      getHiddenColumns: () => {
        const { access } = get()
        if (!access) return []
        const reports = access.sections.find((s) => s.section === 'reports')
        return reports ? reports.hiddenColumns : []
      },
    }),
    {
      name: 'ghostflow-team',
      partialize: (state) => ({
        currentTeamId: state.currentTeamId,
        currentTeam: state.currentTeam,
        currentRole: state.currentRole,
        teams: state.teams,
        // Note: access is NOT persisted — always loaded fresh from API
      }),
    }
  )
)

// =============================================================================
// Selectors
// =============================================================================

export const selectCurrentTeam = (state: TeamState) => state.currentTeam
export const selectCurrentTeamId = (state: TeamState) => state.currentTeamId
export const selectCurrentRole = (state: TeamState) => state.currentRole
export const selectTeams = (state: TeamState) => state.teams
export const selectHasTeamContext = (state: TeamState) => state.currentTeamId !== null
